/**
 * 공급 흐름도 — 노드·엣지 클릭 강조(계보) 순수 함수.
 *
 * 배치·경로(floGeometry.js)와는 관심사가 달라 파일을 분리한다.
 * 이 파일은 좌표를 전혀 모르고, 뷰 모델(buildFloModel 출력)의 nodes/edges 키·s/t 관계만 본다.
 *
 * model.edges 의 s->t 는 물리적 흐름 방향이다(입력 계약의 from->to 를 그대로 옮긴 것).
 * 조상은 t->s 로 거슬러 올라가 찾고, 자손은 s->t 를 그대로 따라가 찾는다.
 * 되돌이(back) 호도 s/t 는 같은 규약이라 똑같이 걷는다.
 * 순환이 있으면 조상·자손 집합이 자연히 겹친다(따로 막지 않는다).
 *
 * 중립 leaf — pages/** 를 import 하지 않는다.
 */

/** 정방향/역방향 인접 목록 — s->[t...], t->[s...]. */
function buildAdjacency(edges) {
  const out = new Map();
  const inn = new Map();
  edges.forEach((e) => {
    if (!out.has(e.s)) out.set(e.s, []);
    out.get(e.s).push(e.t);
    if (!inn.has(e.t)) inn.set(e.t, []);
    inn.get(e.t).push(e.s);
  });
  return { out, inn };
}

/** adj 를 따라 key 에서 도달 가능한 노드 키 집합(자기 자신은 뺀다) — 방문 순서는 결과에 영향 없다. */
function reachable(adj, key) {
  const seen = new Set();
  const stack = [key];
  while (stack.length) {
    const cur = stack.pop();
    for (const next of adj.get(cur) || []) {
      if (!seen.has(next)) {
        seen.add(next);
        stack.push(next);
      }
    }
  }
  return seen;
}

/** u가 조상 집합에 있고 v도 조상 집합에 있거나 pivot 자신이면 그 엣지 인덱스를 담는다. */
function ancestorRuleEdges(edges, pivot, anc, out) {
  edges.forEach((e, idx) => {
    if (anc.has(e.s) && (anc.has(e.t) || e.t === pivot)) out.add(idx);
  });
}

/** u가 자손 집합에 있거나 pivot 자신이고 v도 자손 집합에 있으면 그 엣지 인덱스를 담는다. */
function descendantRuleEdges(edges, pivot, desc, out) {
  edges.forEach((e, idx) => {
    if ((desc.has(e.s) || e.s === pivot) && desc.has(e.t)) out.add(idx);
  });
}

/**
 * 선택(sel)에서 강조할 노드·엣지 집합을 구한다.
 * sel 이 없으면 null(강조 없음 — 아무것도 옅어지지 않는다).
 *
 * 노드 선택({type:"node", key}): nodes = 선택 ∪ 조상 ∪ 자손. 조상의 자손, 자손의 조상은
 * 넣지 않는다(형제는 옅게 남는다). 엣지는 조상 사슬 안쪽(또는 선택으로 들어오는 것)과
 * 자손 사슬 안쪽(또는 선택에서 나가는 것)만 담는다.
 *
 * 엣지 선택({type:"edge", index}, index 는 spec.edges 의 자리): nodes = {s} ∪ anc(s) ∪
 * {t} ∪ desc(t). 엣지는 s 둘레·t 둘레에 같은 두 규칙을 적용한 합집합에 클릭한 엣지 자신을 더한다.
 */
export function lineage(spec, sel) {
  if (!sel) return null;
  const edges = spec?.edges || [];
  const { out, inn } = buildAdjacency(edges);

  if (sel.type === "node") {
    const key = sel.key;
    const anc = reachable(inn, key);
    const desc = reachable(out, key);
    const nodes = new Set([key, ...anc, ...desc]);
    const edgeIdx = new Set();
    ancestorRuleEdges(edges, key, anc, edgeIdx);
    descendantRuleEdges(edges, key, desc, edgeIdx);
    return { nodes, edges: edgeIdx };
  }

  if (sel.type === "edge") {
    const edge = edges[sel.index];
    if (!edge) return { nodes: new Set(), edges: new Set() };
    const { s, t } = edge;
    const ancS = reachable(inn, s);
    const descT = reachable(out, t);
    const nodes = new Set([s, t, ...ancS, ...descT]);
    const edgeIdx = new Set([sel.index]);
    ancestorRuleEdges(edges, s, ancS, edgeIdx);
    descendantRuleEdges(edges, t, descT, edgeIdx);
    return { nodes, edges: edgeIdx };
  }

  return null;
}
